import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Link,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import BugReportIcon from '@mui/icons-material/BugReport';
import GroupsIcon from '@mui/icons-material/Groups';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReplayIcon from '@mui/icons-material/Replay';
import RuleIcon from '@mui/icons-material/Rule';
import SpeedIcon from '@mui/icons-material/Speed';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getCycleScoreboard } from '../../utils/shapeUpApi';

/**
 * One per-user key, applied across cycles: the collapsed/expanded choice is a
 * preference about the page, not about any single cycle.
 */
const SCOREBOARD_KEY = 'shapeup:scoreboardExpanded';

const readExpanded = () => {
  try {
    return localStorage.getItem(SCOREBOARD_KEY) === 'true';
  } catch (err) {
    return false;
  }
};

const writeExpanded = (value) => {
  try {
    localStorage.setItem(SCOREBOARD_KEY, value ? 'true' : 'false');
  } catch (err) {
    // A blocked or full localStorage should not break the toggle.
  }
};

const Metric = ({ icon, value, label, hint, color = 'text.primary' }) => (
  <Box sx={{ minWidth: 132 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {icon}
      <Typography variant="h5" sx={{ fontWeight: 700, color, lineHeight: 1.1 }}>{value}</Typography>
    </Box>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{hint}</Typography>
    )}
  </Box>
);

/**
 * A number is a prompt; the list behind it is what someone acts on. Kept collapsed
 * so the panel stays a glance rather than a report.
 */
const Detail = ({ open, onToggle, closedLabel, openLabel, children }) => (
  <Box sx={{ mt: 1.5 }}>
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
      onClick={onToggle}
    >
      <Typography variant="caption" color="text.secondary">
        {open ? openLabel : closedLabel}
      </Typography>
      <IconButton size="small" sx={{ p: 0.25 }}>
        {open ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Box>
    <Collapse in={open}>{children}</Collapse>
  </Box>
);

/**
 * How a cycle went, in the three numbers Shape Up actually cares about: what was
 * cut, what shipped, and what the team is prepared to call done.
 *
 * Read from stored scopes, so the statuses are as fresh as the last sync of each
 * bet rather than a live view of Jira.
 */
const CycleScoreboard = ({ cycleId, refreshKey = 0 }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHammered, setShowHammered] = useState(false);
  const [showStuck, setShowStuck] = useState(false);
  const [showJudgment, setShowJudgment] = useState(false);
  const [open, setOpen] = useState(() => readExpanded());

  const toggle = () => setOpen((prev) => {
    const next = !prev;
    writeExpanded(next);
    return next;
  });

  const load = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    try {
      setData(await getCycleScoreboard(cycleId));
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load the scoreboard.');
    } finally {
      setLoading(false);
    }
  }, [cycleId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (loading && !data) {
    return <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 3 }} />;
  }
  if (error) {
    return <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>;
  }
  if (!data) return null;

  const {
    scopeCount, hammered, completed, definitionOfDone: dod,
    outcomes, appetite, spread, stuck, judgment, interruptions,
  } = data;
  const pct = (n) => (scopeCount ? Math.round((n / scopeCount) * 100) : 0);

  // A one-line read of the cycle for when the panel is collapsed.
  const summaryParts = [
    `${completed.count}/${scopeCount} completed`,
    `${hammered.count} hammered`,
  ];
  if (dod.configured) summaryParts.push(`${dod.metCount}/${scopeCount} met DoD`);

  // The same active-cycle problems the full panel calls out, distilled to short
  // phrases so they can ride along in the collapsed summary.
  const warnings = [];
  if (stuck?.count > 0) {
    warnings.push(`${stuck.count} ${stuck.count === 1 ? 'dot' : 'dots'} not moving`);
  }
  if (judgment?.staleCount > 0) {
    warnings.push(`${judgment.staleCount} stale ${judgment.staleCount === 1 ? 'hill' : 'hills'}`);
  }
  if (spread?.split?.length > 0) {
    warnings.push(`${spread.split.length} splitting attention`);
  }
  if (outcomes?.returning?.length > 0) {
    warnings.push(`${outcomes.returning.length} returning ${outcomes.returning.length === 1 ? 'bet' : 'bets'}`);
  }
  // Only past budget. One emergency in a cycle is life; being over the number the
  // team set for itself is the thing worth surfacing without being asked.
  if (interruptions?.count > interruptions?.budget) {
    warnings.push(`${interruptions.count} emergencies, over a budget of ${interruptions.budget}`);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
        onClick={toggle}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, letterSpacing: '.08em', color: 'text.secondary' }}
        >
          HOW THIS CYCLE WENT
        </Typography>

        {!open && (
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }} noWrap>
            {summaryParts.join(' · ')}
          </Typography>
        )}

        {!open && warnings.length > 0 && (
          <Tooltip title={warnings.join(' · ')}>
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
              label={`${warnings.length} to review`}
            />
          </Tooltip>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <IconButton size="small" sx={{ p: 0.25 }} aria-label={open ? 'Collapse' : 'Expand'}>
          {open ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
        </IconButton>
      </Box>

      <Collapse in={open}>
      <Stack
        direction="row"
        spacing={3}
        sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 2 }}
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Metric
          icon={<GavelIcon sx={{ fontSize: 18, color: 'warning.main' }} />}
          value={hammered.count}
          label={hammered.count === 1 ? 'scope hammered' : 'scopes hammered'}
          hint="Cut to fit the appetite"
          color="warning.main"
        />

        <Metric
          icon={<CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />}
          value={`${completed.count}/${scopeCount}`}
          label="scopes completed"
          hint={`${pct(completed.count)}% done in Jira`}
          color="success.main"
        />

        <Metric
          icon={<RuleIcon sx={{ fontSize: 18, color: dod.configured ? 'primary.main' : 'text.disabled' }} />}
          value={dod.configured ? `${dod.metCount}/${scopeCount}` : '—'}
          label="met the definition of done"
          hint={dod.configured
            ? `${dod.checksTicked} of ${dod.checksPossible} checks ticked`
            : 'No checklist configured yet'}
          color={dod.configured ? 'primary.main' : 'text.disabled'}
        />

        {/* Completed in Jira but short of the checklist is the number worth
            arguing about, so it is stated rather than left to be worked out. */}
        {dod.configured && completed.withoutDod > 0 && (
          <Metric
            icon={<RuleIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            value={completed.withoutDod}
            label="done in Jira only"
            hint="Completed without meeting the checklist"
            color="text.secondary"
          />
        )}

        {/* The most actionable number on the panel: each one is a conversation
            that has not happened yet. */}
        {stuck?.count > 0 && (
          <Metric
            icon={<HourglassEmptyIcon sx={{ fontSize: 18, color: 'warning.main' }} />}
            value={stuck.count}
            label={stuck.count === 1 ? 'dot not moving' : 'dots not moving'}
            hint={`Still for ${stuck.threshold}+ days`}
            color="warning.main"
          />
        )}

        {/* The work that was not the plan. An honest account of a cycle has to
            include what interrupted it, and the days lost are what make the cost
            of interrupting concrete rather than a feeling people argue about. */}
        {interruptions?.count > 0 && (
          <Metric
            icon={<LocalFireDepartmentIcon sx={{ fontSize: 18, color: 'error.main' }} />}
            value={interruptions.costDays ? `${interruptions.costDays}d` : interruptions.count}
            label={interruptions.costDays
              ? 'lost to emergencies'
              : (interruptions.count === 1 ? 'emergency' : 'emergencies')}
            hint={`${interruptions.count} of a budget of ${interruptions.budget}`}
            color="error.main"
          />
        )}

        {interruptions?.absorbed > 0 && (
          <Metric
            icon={<BugReportIcon sx={{ fontSize: 18, color: 'success.main' }} />}
            value={interruptions.absorbed}
            label={interruptions.absorbed === 1 ? 'bug absorbed' : 'bugs absorbed'}
            hint="Fixed and credited to this cycle"
            color="success.main"
          />
        )}

        {appetite?.measurable > 0 && (
          <Metric
            icon={<SpeedIcon sx={{ fontSize: 18, color: 'info.main' }} />}
            value={`${appetite.withinAppetite}/${appetite.measurable}`}
            label="finished inside the appetite"
            hint={`Of the bets that finished, out of ${appetite.cycleDays} days`}
            color="info.main"
          />
        )}

        {outcomes && (outcomes.shipped + outcomes.circuit_breaker + outcomes.abandoned) > 0 && (
          <Metric
            icon={<FlagOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
            value={outcomes.shipped}
            label={outcomes.shipped === 1 ? 'bet shipped' : 'bets shipped'}
            hint={outcomes.circuit_breaker > 0
              ? `${outcomes.circuit_breaker} hit the circuit breaker`
              : 'Every bet landed'}
          />
        )}
      </Stack>

      {stuck?.count > 0 && (
        <Detail
          open={showStuck}
          onToggle={() => setShowStuck(open => !open)}
          closedLabel={`See the ${stuck.count === 1 ? 'dot' : 'dots'} that stopped moving`}
          openLabel="Hide what stopped moving"
        >
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {stuck.scopes.map(scope => (
              <Box key={scope.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={`${scope.days} days · ${scope.jiraSummary || scope.jiraKey}`}
                />
                <Typography variant="caption" color="text.secondary">
                  {scope.betName}
                  {scope.assigneeName ? ` · ${scope.assigneeName}` : ' · nobody assigned'}
                  {/* Where it stalled changes the question worth asking. */}
                  {scope.position < 0.5 ? ' · still figuring it out' : ' · stalled on the way down'}
                </Typography>
                {scope.jiraUrl && (
                  <Link href={scope.jiraUrl} target="_blank" rel="noopener noreferrer" variant="caption">
                    {scope.jiraKey}
                  </Link>
                )}
              </Box>
            ))}
          </Stack>
        </Detail>
      )}

      {/* Every number above describes the work. This one describes the chart, and
          it belongs first among the caveats: if the hills are not being maintained,
          the figures are a reading of Jira wearing a hill chart's clothes. */}
      {judgment?.staleCount > 0 && (
        <Alert severity="warning" icon={<VisibilityOutlinedIcon fontSize="small" />} sx={{ mt: 1.5, py: 0.25 }}>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {judgment.staleCount === judgment.ratedCount
              ? 'No hill in this cycle has been updated by hand recently'
              : `${judgment.staleCount} of ${judgment.ratedCount} hills have not been updated by hand recently`}
            {judgment.unjudgedScopes > 0 && `, and ${judgment.unjudgedScopes} open scopes have never had their dot placed`}
            .{' '}Positions are coming from Jira, which reports the workflow rather than whether
            the unknowns are resolved. Read the rest of this panel with that in mind.
          </Typography>
          <Detail
            open={showJudgment}
            onToggle={() => setShowJudgment(open => !open)}
            closedLabel="See which hills"
            openLabel="Hide the hills"
          >
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {judgment.hills.filter(hill => hill.isStale).map(hill => (
                <Typography key={hill.betId} variant="caption" color="text.secondary">
                  <strong>{hill.betName}</strong>
                  {hill.judged === 0
                    ? ' — nobody has ever placed a dot'
                    : ` — last placed ${hill.daysSinceJudged} days ago`}
                  {hill.unjudged > 0 && ` · ${hill.unjudged} of ${hill.open} open scopes never placed`}
                </Typography>
              ))}
            </Stack>
          </Detail>
        </Alert>
      )}

      {spread?.split?.length > 0 && (
        <Alert severity="info" icon={<GroupsIcon fontSize="small" />} sx={{ mt: 1.5, py: 0.25 }}>
          {spread.split.map(person => (
            <Typography key={person.userId || person.name} variant="caption" sx={{ display: 'block' }}>
              <strong>{person.name}</strong> is carrying work in {person.betCount} bets
              {' '}({person.bets.join(', ')}). Shape Up gives a whole scope to one person or pair;
              splitting attention usually shows up later as a dot that stops moving.
            </Typography>
          ))}
        </Alert>
      )}

      {spread?.unstaffedBets?.length > 0 && (
        <Alert severity="warning" icon={<GroupsIcon fontSize="small" />} sx={{ mt: 1.5, py: 0.25 }}>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {spread.unstaffedBets.map(bet => bet.name).join(', ')}
            {' '}
            {spread.unstaffedBets.length === 1 ? 'has' : 'have'} work in flight and nobody on
            {' '}
            {spread.unstaffedBets.length === 1 ? 'it' : 'them'}.
          </Typography>
        </Alert>
      )}

      {outcomes?.returning?.length > 0 && (
        <Alert severity="warning" icon={<ReplayIcon fontSize="small" />} sx={{ mt: 1.5, py: 0.25 }}>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {outcomes.returning.map(bet => `${bet.name} (attempt ${bet.attempt})`).join(', ')}
            {' '}has been bet on before. Work that keeps coming back is usually a shaping
            problem rather than an execution one.
          </Typography>
        </Alert>
      )}

      {dod.configured && scopeCount > 0 && (
        <Box sx={{ mt: 2 }}>
          <Tooltip title={`${dod.checksTicked} of ${dod.checksPossible} individual checks across ${scopeCount} scopes`}>
            <LinearProgress
              variant="determinate"
              value={dod.checksPossible ? (dod.checksTicked / dod.checksPossible) * 100 : 0}
              sx={{ height: 6, borderRadius: 3 }}
            />
          </Tooltip>
        </Box>
      )}

      {hammered.count > 0 && (
        <Detail
          open={showHammered}
          onToggle={() => setShowHammered(open => !open)}
          closedLabel="See what was hammered"
          openLabel="Hide what was hammered"
        >
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            {hammered.scopes.map(scope => (
              <Box key={scope.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  variant="outlined"
                  color="warning"
                  label={scope.jiraSummary || scope.jiraKey}
                />
                {scope.reason && (
                  <Typography variant="caption" color="text.secondary">{scope.reason}</Typography>
                )}
                {scope.jiraUrl && (
                  <Link href={scope.jiraUrl} target="_blank" rel="noopener noreferrer" variant="caption">
                    {scope.jiraKey}
                  </Link>
                )}
              </Box>
            ))}
          </Stack>
        </Detail>
      )}
      </Collapse>
    </Paper>
  );
};

export default CycleScoreboard;
